import { Config } from '../core/Config';
import {
    ApiResponse,
    InitResponse,
    Announcement,
    AnnouncementsResponse,
    PaginationMetadata,
    UnreadCountResponse,
    MarkAllReadResponse,
    MetadataResponse,
    UserMetadata,
} from '../types';

/**
 * API version segment prepended to every endpoint. The SDK targets the public
 * RESTful contract at `<apiUrl>/v1/...`, where `apiUrl` is the SDK root (e.g.
 * `https://pulserelease.com/api/sdk`). Kept as a single const so a future
 * version bump is one edit.
 */
const API_VERSION_PREFIX = '/v1';

/**
 * Time budget for the /init request. The customer's page is blocked on this
 * call before any announcement renders, so a vendor outage must not stall it
 * forever. Other endpoints can run unbounded for now since they fire after the
 * page is already interactive.
 */
const INIT_TIMEOUT_MS = 8000;

/**
 * Default time budget for every other request. These fire after the page is
 * interactive, but an unbounded fetch against a hung backend still leaks — and,
 * on the poll loop, lets slow requests stack up. Bound them all.
 */
const DEFAULT_TIMEOUT_MS = 10000;

export class ApiClient {
    private config: Config;
    private sessionToken: string | null = null;

    constructor(config: Config) {
        this.config = config;
    }

    /**
     * Set the per-user session token issued by /init. The client sends it as
     * Authorization: Bearer <token> on subsequent requests so the backend can
     * identify the user without trusting an integer user_id from the body.
     */
    public setSessionToken(token: string | null): void {
        this.sessionToken = token;
    }

    /**
     * Bootstrap the SDK against the unified `POST /v1/init` endpoint (both modes
     * hit the same URL; the server branches on the `guest` flag).
     *
     * Standard mode sends the visitor payload (platform, language, optional
     * identity + metadata) and receives the full session response (session
     * token, theme, plan flags, …). Guest mode sends ONLY `{ guest: true }` — no
     * identity, no anonymous id, no metadata, and never an Authorization token —
     * and receives just the application-level payload (branding `theme`, plan
     * flags), creating no session server-side.
     */
    public async init(anonymousId?: string): Promise<InitResponse> {
        const payload: Record<string, unknown> = this.config.guest
            ? { guest: true }
            : this.buildStandardInitPayload(anonymousId);

        // The endpoint issues the session token, so this must never send one.
        // Sending a stale/invalid persisted token would be rejected by the
        // (fail-closed) backend middleware and brick the recovery path. Guest
        // mode likewise carries no Authorization header. The endpoint returns
        // 201; request() derives success from response.ok, so that's handled.
        const response = await this.request<InitResponse>('POST', '/init', payload, {
            timeoutMs: INIT_TIMEOUT_MS,
            skipAuth: true,
        });
        return response.data!;
    }

    /**
     * Assemble the standard-mode (non-guest) bootstrap body: visitor context
     * plus any claimed identity/metadata. Kept separate so the single init()
     * request path stays mode-agnostic.
     */
    private buildStandardInitPayload(anonymousId?: string): Record<string, unknown> {
        const payload: Record<string, unknown> = {
            platform: this.config.platform,
            language: this.config.language,
        };

        if (this.config.country) {
            payload.country = this.config.country;
        }

        // Build user object if any user data is provided
        if (this.config.user?.id || this.config.user?.email) {
            const user: { id?: string; email?: string } = {};

            if (this.config.user.id) {
                user.id = this.config.user.id.toString();
            }

            if (this.config.user.email) {
                user.email = this.config.user.email;
            }

            payload.user = user;

            // Identity-verification proof for the claimed identity. Only sent
            // alongside an actual identity claim — anonymous sessions assert no
            // identity, so there is nothing to verify (and nothing to sign).
            if (this.config.hash) {
                payload.user_hash = this.config.hash;
            }
        }

        if (!payload.user && anonymousId) {
            payload.anonymous_id = anonymousId;
        }

        if (this.config.metadata && Object.keys(this.config.metadata).length > 0) {
            payload.custom_metadata = this.config.metadata;
        }

        return payload;
    }

    /**
     * Get the full announcement history for list display (including past/ended
     * ones) with pagination. Hits the same `/announcements` collection as the
     * boosted feed but without the `boosted` filter.
     */
    public async getAllAnnouncements(
        language?: string,
        page: number = 1,
        perPage: number = 10
    ): Promise<AnnouncementsResponse> {
        const params: Record<string, string | number | boolean> = {
            language: language || this.config.language,
            platform: this.config.platform,
            page,
            per_page: perPage,
        };

        if (this.config.country) {
            params.country = this.config.country;
        }

        // Pagination now arrives nested under `meta.pagination`; flatten it onto
        // the SDK-internal AnnouncementsResponse shape the UI consumes. Coerce a
        // missing/malformed `announcements` to an empty list so a deterministic
        // payload bug degrades to "nothing to show" instead of throwing into the
        // list UI (which no retry could fix).
        const response = await this.request<{
            announcements: Announcement[];
            meta?: { pagination?: PaginationMetadata };
        }>('GET', '/announcements', params);

        return {
            announcements: response.data?.announcements ?? [],
            pagination: response.data?.meta?.pagination,
        };
    }

    /**
     * Fetch the announcement list for guest (read-only, session-less) mode.
     *
     * Hits the dedicated public guest endpoint `GET {apiUrl}/guest/announcements`
     * (note: NOT under the `/v1` versioned prefix). It carries the same
     * public-key auth as every other call — the `X-Api-Key` header plus origin
     * validation — but sends NO session token and creates NO visitor server-side,
     * so no SdkUser is ever registered for a guest. The response shape matches
     * {@link getAllAnnouncements} (announcements + nested `meta.pagination`) but
     * without any per-user read state.
     */
    public async getGuestAnnouncements(
        language?: string,
        page: number = 1,
        perPage: number = 10
    ): Promise<AnnouncementsResponse> {
        const params: Record<string, string | number | boolean> = {
            language: language || this.config.language,
            platform: this.config.platform,
            page,
            per_page: perPage,
        };

        if (this.config.country) {
            params.country = this.config.country;
        }

        const response = await this.request<{
            announcements: Announcement[];
            meta?: { pagination?: PaginationMetadata };
        }>('GET', '/guest/announcements', params, { skipAuth: true, skipVersion: true });

        return {
            announcements: response.data?.announcements ?? [],
            pagination: response.data?.meta?.pagination,
        };
    }

    /**
     * Get boosted announcements only (for automatic display), via the
     * `boosted=true` filter on the announcements collection.
     */
    public async getBoostedAnnouncements(language?: string): Promise<AnnouncementsResponse> {
        const params: Record<string, string | number | boolean> = {
            boosted: true,
            language: language || this.config.language,
            platform: this.config.platform,
        };

        if (this.config.country) {
            params.country = this.config.country;
        }

        // Preview mode asks the backend to skip the first-visit/read filters so
        // the developer sees every published announcement. Boosted path only.
        if (this.config.preview) {
            params.preview = 1;
        }

        const response = await this.request<AnnouncementsResponse>('GET', '/announcements', params);
        // Tolerate a malformed 2xx (missing announcements) by degrading to an
        // empty feed rather than throwing into the auto-display path.
        return {
            announcements: response.data?.announcements ?? [],
            pagination: response.data?.pagination,
        };
    }

    /**
     * Record a view of an announcement. The view is created as a sub-resource of
     * the announcement and the server returns its `id`, used to attach later
     * click / read-time updates.
     */
    public async trackView(announcementId: string, viewType: string): Promise<{ id: string }> {
        const payload = {
            view_type: viewType,
        };

        const response = await this.request<{ id: string }>(
            'POST',
            `/announcements/${encodeURIComponent(announcementId)}/views`,
            payload
        );
        return response.data!;
    }

    /**
     * Apply a partial update to an existing view resource. Both click and
     * read-time tracking are expressed as PATCHes of the same `/views/{view}`
     * resource, each sending only the field it owns.
     */
    private async updateView(viewId: string, body: Record<string, unknown>): Promise<void> {
        await this.request('PATCH', `/views/${encodeURIComponent(viewId)}`, body);
    }

    public async trackClick(viewId: string): Promise<void> {
        await this.updateView(viewId, { clicked: true });
    }

    public async trackReadTime(viewId: string, seconds: number): Promise<void> {
        await this.updateView(viewId, { read_seconds: seconds });
    }

    public async markAsRead(announcementId: string): Promise<UnreadCountResponse> {
        const response = await this.request<UnreadCountResponse>(
            'PUT',
            `/announcements/${encodeURIComponent(announcementId)}/read`
        );
        return response.data!;
    }

    public async markAllAsRead(): Promise<MarkAllReadResponse> {
        const response = await this.request<MarkAllReadResponse>('PUT', '/announcements/read');
        return response.data!;
    }

    public async getUnreadCount(): Promise<number> {
        const params: Record<string, string | number | boolean> = {
            language: this.config.language,
            platform: this.config.platform,
        };

        if (this.config.country) {
            params.country = this.config.country;
        }

        const response = await this.request<UnreadCountResponse>(
            'GET',
            '/announcements/unread-count',
            params
        );
        return response.data!.unread_count;
    }

    /**
     * Merge a map of custom metadata props into the current session's visitor.
     * The body is the props map directly (e.g. `{ plan: 'enterprise', seats: 12 }`).
     * The session token attaches automatically via the shared `request()` path.
     */
    public async patchMetadata(props: UserMetadata): Promise<MetadataResponse> {
        const response = await this.request<MetadataResponse>('PATCH', '/metadata', props);
        return response.data!;
    }

    /**
     * Remove a single custom metadata key from the current session's visitor.
     * The key is URL-encoded into the path.
     */
    public async deleteMetadataKey(key: string): Promise<MetadataResponse> {
        const response = await this.request<MetadataResponse>(
            'DELETE',
            '/metadata/' + encodeURIComponent(key)
        );
        return response.data!;
    }

    /**
     * Clear all custom metadata for the current session's visitor.
     */
    public async clearAllMetadata(): Promise<MetadataResponse> {
        const response = await this.request<MetadataResponse>('DELETE', '/metadata');
        return response.data!;
    }

    private async request<T = unknown>(
        method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
        endpoint: string,
        data?: Record<string, unknown>,
        options: { timeoutMs?: number; skipAuth?: boolean; skipVersion?: boolean } = {}
    ): Promise<ApiResponse<T>> {
        // build the URL with query parameters for GET requests, handle double /.
        // Every endpoint is versioned under `<apiUrl>/v1`, except the public
        // guest surface which lives outside the versioned API (skipVersion).
        endpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
        const cleanedApiUrl = this.config.apiUrl.endsWith('/')
            ? this.config.apiUrl.slice(0, -1)
            : this.config.apiUrl;

        const versionPrefix = options.skipVersion ? '' : API_VERSION_PREFIX;
        const url = new URL(cleanedApiUrl + versionPrefix + endpoint);

        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'X-Api-Key': this.config.apiKey,
        };

        if (this.sessionToken && !options.skipAuth) {
            headers['Authorization'] = `Bearer ${this.sessionToken}`;
        }

        const fetchOptions: RequestInit = {
            method,
            headers,
        };

        if (method === 'GET' && data) {
            Object.keys(data).forEach((key) => {
                url.searchParams.append(key, String(data[key]));
            });
        } else if (method !== 'GET' && data) {
            // POST/PATCH carry a JSON body; a bodyless DELETE passes no `data`
            // and therefore sends no body.
            fetchOptions.body = JSON.stringify(data);
        }

        // Wire an AbortController so a hung backend doesn't leave the customer's
        // page (or the poll loop) waiting indefinitely. Every request is bounded;
        // callers can override the budget via options.timeoutMs.
        const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
        let abortController: AbortController | null = null;
        let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
        if (timeoutMs && typeof AbortController !== 'undefined') {
            abortController = new AbortController();
            fetchOptions.signal = abortController.signal;
            timeoutHandle = setTimeout(() => abortController!.abort(), timeoutMs);
        }

        try {
            this.config.log(`API Request: ${method} ${url.toString()}`);

            const response = await fetch(url.toString(), fetchOptions);

            if (!response.ok) {
                // Read the error body defensively: an upstream 502/504 often
                // returns HTML (or nothing at all), and calling json() on that
                // would throw and swallow the HTTP status the session-recovery
                // path (401) depends on. The server speaks Laravel-native errors
                // (`{ message, errors }`); `error` is only a legacy fallback.
                let errorBody: {
                    message?: string;
                    error?: string;
                    errors?: Record<string, string[]>;
                } | null = null;
                try {
                    errorBody = await response.json();
                } catch {
                    // Non-JSON / empty error body — the status alone is enough.
                }

                this.config.error(`API Error: ${response.status}`, errorBody);
                // Preserve the HTTP status on the error so callers can react to
                // an expired/missing session (401) by recovering it instead of
                // failing silently forever.
                const apiError = new Error(
                    errorBody?.message ||
                        errorBody?.error ||
                        `API request failed (${response.status})`
                ) as Error & {
                    status?: number;
                };
                apiError.status = response.status;
                throw apiError;
            }

            // A successful response may still carry a non-JSON or empty body
            // (e.g. a 204 from a fire-and-forget track endpoint); treat that as
            // "no data" rather than throwing into the caller.
            let json: T | null = null;
            try {
                json = await response.json();
            } catch {
                json = null;
            }

            this.config.log(`API Response:`, json);

            return {
                success: true,
                data: json ?? undefined,
            };
        } catch (error) {
            // Re-shape AbortError so callers (and customers reading logs) see
            // a clear "timed out" instead of a generic AbortError.
            if (
                timeoutMs &&
                error instanceof Error &&
                (error.name === 'AbortError' || abortController?.signal.aborted)
            ) {
                const timeoutError = new Error(
                    `Request to ${endpoint} timed out after ${timeoutMs}ms`
                );
                timeoutError.name = 'TimeoutError';
                this.config.error(`API Request Timeout:`, timeoutError);
                throw timeoutError;
            }

            this.config.error(`API Request Failed:`, error);
            throw error;
        } finally {
            if (timeoutHandle) {
                clearTimeout(timeoutHandle);
            }
        }
    }
}
